import { Check, Plus, X } from 'lucide-react';
import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '@/components/shared/Spinner';
import { useExtractionUpload } from '@/features/Quote/hooks/useExtractionUpload';
import { cn } from '@/lib/utils';
import apiService from '@/services/apiService';
import type { UnreadableReason } from '@/types/app';

// Mirrors the accepted types in config/filepond.json for the policy upload.
const ACCEPTED_FILE_TYPES = 'application/pdf,image/jpeg,image/png,image/heic,image/heif';
const UPLOAD_CARD_CONTAINER_CLASS = 'relative min-h-38 w-full rounded-lg!';
const UPLOAD_CARD_PANEL_CLASS = 'absolute inset-0 h-full w-full rounded-lg!';

// The reasons the backend can attach to an *unsuccessful add-later upload*: it
// keeps the existing offer and sets one of these (see backend
// `extract_policy_document_later`). We surface any of them as the dropzone's
// inline error state, mapped to a short message (see REASON_COPY_BUCKET). Other
// UnreadableReason values (KVK_UNAVAILABLE, SME_ABSENCE_RELIEF) belong to the
// full-page error/initial-submission flow and must not render here, so we guard
// against them.
const BACKFILL_UNREADABLE_REASONS = [
	'NO_POLICY_DETECTED',
	'WRONG_PRODUCT_TYPE',
	'INCOMPLETE_DOCUMENT',
	'PROTECTED_PDF',
	'TECHNICAL_FAILURE'
] as const;

type BackfillUnreadableReason = (typeof BACKFILL_UNREADABLE_REASONS)[number];

const isBackfillUnreadableReason = (
	reason: UnreadableReason | null | undefined
): reason is BackfillUnreadableReason =>
	!!reason && (BACKFILL_UNREADABLE_REASONS as readonly UnreadableReason[]).includes(reason);

// The backend keeps five reason codes (useful for logging), but from the user's
// point of view there are only three things to say + do: unlock the PDF, upload a
// proper policy, or wait. Collapse the three "couldn't read a usable policy"
// reasons into one concise message.
const REASON_COPY_BUCKET: Record<BackfillUnreadableReason, string> = {
	PROTECTED_PDF: 'protectedPdf',
	NO_POLICY_DETECTED: 'noPolicy',
	WRONG_PRODUCT_TYPE: 'noPolicy',
	INCOMPLETE_DOCUMENT: 'noPolicy',
	TECHNICAL_FAILURE: 'technical'
};

// The rounded dashed outline shared by every dropzone state; its colour follows
// the panel's `currentColor`. The rect is inset by the stroke's outer half (1px)
// so the 2px stroke lands entirely inside the panel's rounded box. That inset
// size has to come from CSS: `calc()` is not a valid SVG *attribute* value, and
// browsers log a parse error for it (WebKit: `Invalid value for <rect>
// attribute height`), so it is set through the geometry properties instead.
const DashedBorder = () => (
	<svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
		<rect
			x="1"
			y="1"
			className="h-[calc(100%-2px)] w-[calc(100%-2px)]"
			rx="8"
			ry="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeDasharray="6 6"
		/>
	</svg>
);

interface PolicyDocumentUploadProps {
	handleTriggerAppStatePolling?: () => void;
	/**
	 * When an earlier add-later upload could not be read, the backend keeps the
	 * existing offer and sets this categorized reason; we render the dropzone in its
	 * inline error state (rather than discarding the offer) so the user can retry in
	 * place. Cleared by the backend on a successful upload.
	 */
	unreadableReason?: UnreadableReason | null;
	/**
	 * Disable the upload while quotes are being (re)fetched (status
	 * PROCESSING_QUOTES) to avoid racing a concurrent recompute with a new
	 * extraction. The backend also 409s a second upload while one is in flight;
	 * this stops the user triggering that in the first place.
	 */
	disabled?: boolean;
	/**
	 * The add-later upload was read successfully: the backend parsed the policy
	 * and the recompute is now fetching quotes. The parent detects this (parsed
	 * SLST coverage arrived) and keeps this widget mounted so we can show a green
	 * confirmation — the success counterpart of the inline error state — until the
	 * quotes are ready and the current-policy card takes over.
	 */
	succeeded?: boolean;
}

/**
 * "Add your current policy later" CTA shown in the sidebar's "Current Policy"
 * card when a form was started via no-policy / initial-payload (issue #961). A
 * clear primary (blue) button opens the file picker; on upload the backend locks
 * the offer (PROCESSING_QUOTES) and extracts + recomputes server-side. We restart
 * app-state polling so the quotes view shows its loading state and reopens with
 * the recomputed quotes — at which point this CTA hides (the form now has a
 * current-policy document). Completion is therefore driven entirely by app-state
 * polling, so this component does not poll an extraction-status endpoint itself.
 *
 * A failed add-later upload does NOT discard the offer: the backend keeps the
 * quotes, drops the document so a retry is allowed, and sets `unreadableReason`.
 * Both that and a client-side upload error render the dropzone's inline error
 * state ("something went wrong, try again"). That state carries a close (X)
 * affordance so the user can dismiss it and return to the upload button to retry.
 */
const PolicyDocumentUpload = ({
	handleTriggerAppStatePolling,
	unreadableReason,
	disabled = false,
	succeeded = false
}: PolicyDocumentUploadProps) => {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	// Locally dismiss the inline error (the X affordance) to reveal the upload
	// button again. A client-side `error` clears via `setError`, but a backend
	// `unreadableReason` persists as a prop until the next successful upload, so we
	// track the dismissal here. Reset whenever a fresh upload begins.
	const [errorDismissed, setErrorDismissed] = useState(false);

	const { isProcessing, error, setError, handleProcessFile, stopProcessing } = useExtractionUpload({
		upload: apiService.policy.uploadDocumentLater,
		onUploaded: () => {
			// The server locked the offer to PROCESSING_QUOTES; restart app-state
			// polling so the quotes view shows the loading state and the now-stale
			// quotes become unselectable while the backend recomputes.
			handleTriggerAppStatePolling?.();
		}
	});

	// The policy upload has no status endpoint (useExtractionUpload skips polling for
	// this flow), so `isProcessing` stays true after the POST and the in-place spinner
	// spans the whole server-side recompute — the parent keeps this widget mounted
	// meanwhile (see Sidebar's upload latch). End the spinner exactly when that
	// recompute settles, i.e. once `disabled` (PROCESSING_QUOTES) has gone true and
	// then false again: a rejection has by then set `unreadableReason`, so the inline
	// error takes over. On a success the parent instead parses the policy coverage and
	// keeps us mounted with `succeeded`, so the green confirmation (rendered with
	// priority over this spinner) shows until the quotes are ready and the widget
	// unmounts. Keying on the disabled transition (not on `unreadableReason`) avoids
	// reacting to a stale reason from a prior failed upload during a retry.
	const sawFetchRef = useRef(false);
	useEffect(() => {
		if (!isProcessing) {
			sawFetchRef.current = false;
			return;
		}
		if (disabled) {
			sawFetchRef.current = true;
		} else if (sawFetchRef.current) {
			stopProcessing();
		}
	}, [isProcessing, disabled, stopProcessing]);

	const uploadFile = (file?: File) => {
		if (!file || disabled) return;
		setError(null);
		setErrorDismissed(false);
		// Reuse the shared upload lifecycle (FilePond's process signature).
		handleProcessFile(
			'file',
			file,
			{},
			() => {},
			() => {}
		);
	};

	const handleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		// Allow re-selecting the same file later (onChange won't fire otherwise).
		event.target.value = '';
		uploadFile(file);
	};

	const handleDragOver = (event: DragEvent<HTMLElement>) => {
		event.preventDefault();
		if (disabled) return;
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const handleDrop = (event: DragEvent<HTMLElement>) => {
		event.preventDefault();
		setIsDragging(false);
		if (disabled) return;
		uploadFile(event.dataTransfer.files[0]);
	};

	// A failed upload — either a fresh client-side error or an earlier add-later
	// extraction the backend could not read (it keeps the offer and sets a
	// categorized `unreadableReason`). Both surface as the dropzone's inline error
	// state, which the user can dismiss (the X) to fall back to the upload button.
	// Hidden while a new upload is in flight; reasons that belong to the full-page
	// flow are ignored by the guard.
	//
	// A backend failure carries a categorized reason we can explain specifically
	// (e.g. "PDF is password protected — download an unprotected copy"); a generic
	// client-side upload error has none, so we fall back to generic copy.
	const reasonKey = isBackfillUnreadableReason(unreadableReason) ? unreadableReason : null;
	// Never surface an error while a recompute is in flight (`disabled`): the offer
	// is being refetched, so show the waiting state instead of a stale reason.
	const showError =
		!isProcessing && !disabled && !errorDismissed && (!!error || reasonKey !== null);

	const dismissError = () => {
		setError(null);
		setErrorDismissed(true);
	};

	// Resolve the error copy: a backend reason maps to one of three short messages
	// (title + optional body); a client-side error falls back to generic copy.
	const errorBucket = reasonKey ? REASON_COPY_BUCKET[reasonKey] : null;
	const errorTitle = errorBucket
		? t(`quotes.settings.coverage.upload.errors.${errorBucket}.title`)
		: t('quotes.settings.coverage.upload.errorTitle');
	const errorBody = errorBucket
		? t(`quotes.settings.coverage.upload.errors.${errorBucket}.body`)
		: t('quotes.settings.coverage.upload.errorRetry');

	return (
		<div data-testid="policy-upload-view" className="mb-4">
			{!isProcessing && (
				<input
					ref={inputRef}
					type="file"
					accept={ACCEPTED_FILE_TYPES}
					className="hidden"
					onChange={handleFileSelected}
					disabled={disabled}
					data-testid="policy-upload-input"
				/>
			)}

			<div className={UPLOAD_CARD_CONTAINER_CLASS}>
				{succeeded ? (
					// Success counterpart of the inline error state: the backend read the
					// policy and quote-fetching has begun. In-flow (not absolute) so the
					// panel matches the error panel's height, with a green checkmark badge.
					<div
						className="relative flex min-h-38 w-full flex-col items-center justify-center rounded-lg! bg-white px-4 py-6 text-center text-green-500/35"
						data-testid="policy-upload-success-alert"
					>
						<DashedBorder />
						<span className="mb-4 flex size-10 aspect-square shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
							<Check className="h-5 w-5" aria-hidden="true" />
						</span>
						<span className="text-base font-bold text-foreground">
							{t('quotes.settings.coverage.upload.successTitle')}
						</span>
						<span className="mt-1 text-xs font-medium text-muted-foreground">
							{t('quotes.settings.coverage.upload.successBody')}
						</span>
					</div>
				) : isProcessing ? (
					<div
						className={cn(
							UPLOAD_CARD_PANEL_CLASS,
							'flex flex-col items-center justify-center bg-blue-50 px-4 py-6 text-center text-blue-300'
						)}
					>
						<DashedBorder />
						<span className="mb-4 flex size-10 items-center justify-center">
							<Spinner size="lg" variant="current" className="opacity-75" visible={isProcessing} />
						</span>
						<span className="text-base font-bold text-foreground">
							{t('quotes.settings.coverage.upload.processing')}
						</span>
						<span className="mt-1 text-xs font-medium text-muted-foreground">
							{t('quotes.settings.coverage.upload.drop')}
						</span>
					</div>
				) : showError ? (
					// In-flow (not absolute) so the panel grows to fit the reason copy,
					// which can run to a few lines.
					<div
						className="relative flex min-h-38 w-full flex-col items-center justify-center rounded-lg! bg-white px-4 py-6 text-center text-destructive/35"
						data-testid="policy-upload-error-alert"
					>
						<DashedBorder />
						<button
							type="button"
							onClick={dismissError}
							className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-red-100"
							aria-label={t('quotes.settings.coverage.upload.errorDismiss')}
							data-testid="policy-upload-error-dismiss"
						>
							<X className="h-4 w-4" aria-hidden="true" />
						</button>
						<span className="mb-4 flex size-10 aspect-square shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
							<X className="h-5 w-5" aria-hidden="true" />
						</span>
						<span className="text-base font-bold text-foreground">{errorTitle}</span>
						{errorBody && (
							<span className="mt-1 text-xs font-medium text-muted-foreground">{errorBody}</span>
						)}
					</div>
				) : (
					<button
						type="button"
						disabled={disabled}
						aria-disabled={disabled}
						className={cn(
							UPLOAD_CARD_PANEL_CLASS,
							'group flex flex-col items-center justify-center bg-white px-4 py-6 text-center text-primary/35 transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-neutral-100',
							disabled
								? 'cursor-not-allowed opacity-60'
								: 'cursor-pointer hover:bg-primary/5 hover:text-primary/50',
							!disabled && isDragging && 'bg-primary/5 text-primary'
						)}
						onClick={() => {
							if (disabled) return;
							inputRef.current?.click();
						}}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						data-testid="policy-upload-button"
					>
						<DashedBorder />
						<span className="mb-4 flex size-10 aspect-square shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:scale-105">
							<Plus className="h-5 w-5" aria-hidden="true" />
						</span>
						<span className="text-base font-bold text-foreground">
							{t('quotes.settings.coverage.upload.button')}
						</span>
						<span className="mt-1 text-xs font-medium text-muted-foreground">
							{disabled
								? t('quotes.settings.coverage.upload.disabledHint')
								: t('quotes.settings.coverage.upload.drop')}
						</span>
					</button>
				)}
			</div>
		</div>
	);
};

export default PolicyDocumentUpload;
